/**
 * AddOperationalNoteDialog — Version 1.1 · PO directive 2026-08-01.
 *
 * Lightweight side-action that lets an operator capture "something the
 * system cannot infer" without leaving the current surface. Not part of
 * the required "Start the Day" workflow.
 *
 * Contract:
 *   • Free-text only. No categories, no metadata.
 *   • On submit → POST /api/operational-notes.
 *   • STD-009 compliant: loading state · toast success/failure ·
 *     explicit next action.
 *
 * Usage:
 *   <AddOperationalNoteDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     onSaved={() => reload()}
 *     testidPrefix="ops-add-note" />
 */
import { useState } from "react";
import { toast } from "sonner";
import { PencilLine, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function AddOperationalNoteDialog({
  open,
  onOpenChange,
  onSaved,
  testidPrefix = "add-operational-note",
}) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const close = () => {
    if (saving) return;
    setText("");
    onOpenChange(false);
  };

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error("Please write a note before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/operational-notes", { text: trimmed });
      toast.success("Operational note added. Visible on the Morning Brief.");
      setText("");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={close}
      data-testid={`${testidPrefix}-backdrop`}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testidPrefix}-dialog`}
      >
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="rounded-md bg-slate-100 p-2 text-slate-700">
            <PencilLine size={18} weight="fill" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900">Add Operational Note</h2>
            <p className="text-xs text-slate-500">
              Anything affecting today's operations that the system cannot infer automatically.
            </p>
          </div>
          <button
            onClick={close}
            disabled={saving}
            data-testid={`${testidPrefix}-close`}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4">
          <textarea
            rows={5}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Truck 15-23 is in the shop today · Ministry inspection at 2 PM · Office closes early…"
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
            data-testid={`${testidPrefix}-textarea`}
          />
          <p className="mt-2 text-[11px] text-slate-500">
            Notes are read-only once saved. They surface on today's Morning Brief.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <button
            onClick={close}
            disabled={saving}
            data-testid={`${testidPrefix}-cancel`}
            className="rounded-md px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !text.trim()}
            data-testid={`${testidPrefix}-submit`}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Note"}
          </button>
        </div>
      </div>
    </div>
  );
}
